import { computed, ref, type Ref } from 'vue'

export type Locale = 'en' | 'ms'

/**
 * Generic i18n runtime, shared by every Pinch Crew app.
 *
 * Each app registers its OWN message bundles — the worker PWA and the
 * employer console have almost no copy in common, and forcing one bundle
 * would make both harder to translate. The runtime only owns locale
 * detection, persistence, interpolation and pluralisation.
 */

const STORAGE_KEY = 'pinchcrew.locale'

export const locale = ref<Locale>(detectLocale())

function detectLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved === 'en' || saved === 'ms') return saved
  } catch { /* private mode */ }
  return navigator.language?.toLowerCase().startsWith('ms') ? 'ms' : 'en'
}

export function setLocale(next: Locale): void {
  locale.value = next
  try { localStorage.setItem(STORAGE_KEY, next) } catch { /* private mode */ }
  if (typeof document !== 'undefined') document.documentElement.lang = next
}

/**
 * Maps every leaf of a bundle to `string`, preserving key structure.
 *
 * `typeof en` alone would make each value a literal type (`'Back'`), which no
 * translation can satisfy; this keeps key checking exact while allowing any
 * translated value.
 */
export type Translated<T> = { [K in keyof T]: T[K] extends string ? string : Translated<T[K]> }

function resolve(bundle: unknown, path: string): string {
  const value = path.split('.').reduce<unknown>(
    (acc, key) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : undefined),
    bundle,
  )
  return typeof value === 'string' ? value : path
}

export interface I18n<B> {
  t: (path: string, params?: Record<string, string | number>) => string
  locale: Ref<Locale>
  setLocale: (next: Locale) => void
  messages: Ref<B>
}

/**
 * Builds an app's translator from its own bundles.
 *
 * Supports `{placeholder}` interpolation and `'one | many'` plural forms,
 * selected by `params.n`.
 */
export function createI18n<B>(bundles: Record<Locale, B>): I18n<B> {
  function t(path: string, params?: Record<string, string | number>): string {
    let out = resolve(bundles[locale.value], path)

    if (out.includes('|')) {
      const forms = out.split('|').map(s => s.trim())
      const n = Number(params?.n ?? 0)
      out = (n === 1 ? forms[0] : forms[1]) ?? forms[0] ?? ''
    }

    if (params) {
      for (const [key, value] of Object.entries(params)) {
        out = out.replaceAll(`{${key}}`, String(value))
      }
    }
    return out
  }

  return {
    t,
    locale,
    setLocale,
    messages: computed(() => bundles[locale.value]) as Ref<B>,
  }
}
